import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Grid, Drawer, Layout, Button, Typography, Space } from 'antd';
import { CloseOutlined, ExportOutlined } from '@ant-design/icons';
import { useCustomerWorkspace } from '../contexts/CustomerWorkspaceContext';

import './CustomerWorkspaceShell.css';

const { Content: SplitMain, Sider: WorkspaceSider } = Layout;
const { Text } = Typography;

function PanelHeader({ meta, onClose, onTimelineNavigate }) {
  return (
    <div className="varexe-right-rail__header">
      <Text strong style={{ fontSize: 14 }}>
        {meta.title || 'Workspace'}
      </Text>
      <Space size="small">
        {meta.timelineHref ? (
          <Link
            to={meta.timelineHref}
            onClick={onTimelineNavigate}
            className="varexe-right-rail__timeline-link"
          >
            <ExportOutlined /> Open full timeline
          </Link>
        ) : null}
        {onClose ? (
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={onClose}
            aria-label="Close panel"
          />
        ) : null}
      </Space>
    </div>
  );
}

/**
 * Workspace template:
 * - Wide + open: bounded split with Sider rail above the footer; header trigger toggles open/close.
 * - Wide + closed: full-width main; header trigger has badge + opens.
 * - Narrow: drawer toggled by the header trigger.
 */
const CustomerWorkspaceShell = ({ children }) => {
  const location = useLocation();
  const {
    rightSlot,
    rightPanelOpen,
    setRightPanelOpen,
    mobileRailOpen,
    setMobileRailOpen,
    setDetailSplitLayout,
    panelMeta,
  } = useCustomerWorkspace();
  const screens = Grid.useBreakpoint();
  const isWide = Boolean(screens.lg);

  const hasRight = Boolean(rightSlot);
  const wideOpen = isWide && hasRight && rightPanelOpen;

  useEffect(() => {
    setMobileRailOpen(false);
  }, [location.pathname, setMobileRailOpen]);

  useEffect(() => {
    setDetailSplitLayout(wideOpen);
    return () => setDetailSplitLayout(false);
  }, [wideOpen, setDetailSplitLayout]);

  if (isWide) {
    return (
      <Layout
        className={
          wideOpen
            ? 'varexe-workspace-detail-split varexe-workspace-detail-split--with-rail'
            : 'varexe-workspace-detail-split'
        }
      >
        <SplitMain className="varexe-workspace-detail-split__main">
          <div className="varexe-workspace-page">{children}</div>
        </SplitMain>
        {wideOpen ? (
          <WorkspaceSider
            theme="light"
            width={360}
            className="varexe-workspace-detail-split__sider"
          >
            <div className="varexe-right-rail varexe-right-rail--docked">
              <PanelHeader meta={panelMeta} onClose={() => setRightPanelOpen(false)} />
              <div className="varexe-right-rail__body">{rightSlot}</div>
            </div>
          </WorkspaceSider>
        ) : (
          <div className="varexe-workspace-detail-split__rail-spacer" aria-hidden="true" />
        )}
      </Layout>
    );
  }

  if (!hasRight) {
    return <div className="varexe-workspace-page">{children}</div>;
  }

  return (
    <>
      <div className="varexe-workspace-page">{children}</div>
      <Drawer
        title={null}
        placement="right"
        size="100%"
        open={mobileRailOpen}
        onClose={() => setMobileRailOpen(false)}
        destroyOnHidden={false}
        styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', height: '100%' } }}
        rootClassName="varexe-mobile-workspace-drawer"
      >
        <div className="varexe-right-rail varexe-right-rail--drawer">
          <PanelHeader
            meta={panelMeta}
            onClose={() => setMobileRailOpen(false)}
            onTimelineNavigate={() => setMobileRailOpen(false)}
          />
          <div className="varexe-right-rail__body">{rightSlot}</div>
        </div>
      </Drawer>
    </>
  );
};

export default CustomerWorkspaceShell;
